import {
  invoiceDueDate,
  normalizePaymentTermsInDays,
  toDateOnly,
} from './invoicePaymentTerms';

/** Local only. The new-tab preview reads this. It is not a transmission or a saved document. */
export const OFFLINE_PUBLIC_DOC_PREVIEW_STORAGE_KEY = 'jarvis.offlinePublicDocPreview';
export const OFFLINE_PUBLIC_DOC_PREVIEW_PATH = '/public/doc/preview';

export type OfflinePublicDocLine = {
  name?: string | null;
  qty?: number | null;
  metaJson?: unknown;
};

export type OfflinePublicDocSource = {
  kind: string;
  status?: string | null;
  acceptedAt?: string | null;
  lifecycleRef?: string | null;
  invoiceRef?: string | null;
  title?: string | null;
  description?: string | null;
  currency?: string | null;
  vatEnabled?: boolean | null;
  netTotal?: number | null;
  vatAmount?: number | null;
  grossTotal?: number | null;
  issuedAt?: string | null;
  invoiceRole?: string | null;
  paymentTermsInDays?: number | null;
  projectDays?: number | null;
  projectHours?: number | null;
  hasDeposit?: boolean | null;
  depositAmount?: number | null;
  depositPercentage?: number | null;
  versionNumber?: number | null;
  organisationName?: string | null;
  customer?: {
    title?: string | null;
    forename?: string | null;
    surname?: string | null;
    address?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
  visualDesignerLines?: OfflinePublicDocLine[];
  pdfHtml?: string | null;
  finalSurveyReapproval?: Record<string, unknown> | null;
};

/** Customer-page payload from the saved document. No share link, transmission, or open log. */
export function buildOfflinePublicDocPreview(source: OfflinePublicDocSource) {
  const kind = String(source.kind || '');
  const isInvoice = kind.trim().toLowerCase() === 'invoice';
  const issuedAt = source.issuedAt ?? null;
  const termsDays = isInvoice
    ? normalizePaymentTermsInDays(source.paymentTermsInDays, source.invoiceRole)
    : null;
  const issuedDateOnly = toDateOnly(issuedAt);
  const lines = Array.isArray(source.visualDesignerLines) ? source.visualDesignerLines : [];

  return {
    shareLinkId: 'offline-preview',
    sentVersionId: null,
    sentVersionNumber: !isInvoice && source.versionNumber != null ? source.versionNumber : null,
    isOutdatedQuote: false,
    sentAt: null,
    validUntil: null,
    emailActivityId: null,
    openLogged: false,
    openedAt: null,
    viewerKind: 'customer' as const,
    organisationName: source.organisationName ?? null,
    messages: [],
    customer: {
      title: source.customer?.title ?? null,
      forename: source.customer?.forename ?? null,
      surname: source.customer?.surname ?? null,
      address: source.customer?.address ?? null,
      email: source.customer?.email ?? null,
      phone: source.customer?.phone ?? null,
    },
    document: {
      kind,
      status: source.status ?? null,
      acceptedAt: source.acceptedAt ?? null,
      lifecycleRef: source.lifecycleRef || '',
      invoiceRef: source.invoiceRef ?? null,
      title: source.title || '',
      description: source.description ?? null,
      currency: source.currency || 'GBP',
      vatEnabled: source.vatEnabled ?? null,
      totals: {
        netTotal: source.netTotal ?? null,
        vatAmount: source.vatAmount ?? null,
        grossTotal: source.grossTotal ?? null,
      },
      issuedAt,
      invoiceRole: source.invoiceRole ?? null,
      paymentTermsInDays: termsDays,
      dueDate: isInvoice && issuedDateOnly && termsDays != null
        ? invoiceDueDate(issuedDateOnly, termsDays)
        : null,
      projectDuration: {
        days: source.projectDays ?? null,
        hours: source.projectHours ?? null,
      },
      deposit: {
        hasDeposit: !!source.hasDeposit,
        depositAmount: source.depositAmount ?? null,
        depositPercentage: source.depositPercentage ?? null,
      },
      visualDesignerComponents: lines.map((line) => ({
        name: line?.name ?? null,
        qty: line?.qty ?? null,
        meta: line?.metaJson ?? {},
      })),
    },
    pdf: { url: null, html: source.pdfHtml || null },
    finalSurveyReapproval: source.finalSurveyReapproval ?? null,
  };
}

/** Saved Final delta, shaped like the public page. Only customer-reapproval records. */
export function finalReapprovalFromSavedDelta(row: any): Record<string, unknown> | null {
  if (!row?.id) return null;
  if (row.superseded_at) return null;
  const classification = String(row.classification || '').trim();
  if (classification !== 'customer_reapproval_required' && classification !== 'pending_review') return null;
  const snapshot = row.snapshot_json && typeof row.snapshot_json === 'object'
    ? row.snapshot_json
    : (row.snapshot && typeof row.snapshot === 'object' ? row.snapshot : null);
  const changes = snapshot?.changes && typeof snapshot.changes === 'object' ? snapshot.changes : null;
  const review = Array.isArray(changes?.review)
    ? changes.review.map((section: any) => ({
      id: String(section?.id || ''),
      title: String(section?.title || ''),
      rows: Array.isArray(section?.rows)
        ? section.rows.map((reviewRow: any) => ({
          id: String(reviewRow?.id || ''),
          group: String(reviewRow?.group || ''),
          item: reviewRow?.item != null && String(reviewRow.item).trim() ? String(reviewRow.item) : null,
          label: String(reviewRow?.label || ''),
          original: reviewRow?.original != null && String(reviewRow.original).trim() ? String(reviewRow.original) : null,
          final: reviewRow?.final != null && String(reviewRow.final).trim() ? String(reviewRow.final) : null,
        }))
        : [],
    })).filter((section: { rows: unknown[] }) => section.rows.length > 0)
    : undefined;

  return {
    deltaId: String(row.id),
    finalSurveyId: row.final_survey_id ? String(row.final_survey_id) : (row.finalSurveyId ? String(row.finalSurveyId) : null),
    classification,
    pending: !row.customer_accepted_at && !row.customerAcceptedAt,
    acceptedAt: row.customer_accepted_at
      ? String(row.customer_accepted_at)
      : (row.customerAcceptedAt ? String(row.customerAcceptedAt) : null),
    comparedAgainst: snapshot?.comparedAgainst === 'previous_final' ? 'previous_final' : 'first_survey',
    first: snapshot?.first && typeof snapshot.first === 'object'
      ? { header: snapshot.first.header || {}, lines: Array.isArray(snapshot.first.lines) ? snapshot.first.lines : [] }
      : null,
    final: snapshot?.final && typeof snapshot.final === 'object'
      ? { header: snapshot.final.header || {}, lines: Array.isArray(snapshot.final.lines) ? snapshot.final.lines : [] }
      : null,
    changes: changes
      ? {
        notes: Array.isArray(changes.notes) ? changes.notes.map((note: any) => String(note)) : [],
        headerFieldDiffs: Array.isArray(changes.headerFieldDiffs)
          ? changes.headerFieldDiffs.map((note: any) => String(note))
          : [],
        visualDimensionalChanged: !!changes.visualDimensionalChanged,
        commercialLineCountChanged: !!changes.commercialLineCountChanged,
        materialScopeOrPricingChanged: !!changes.materialScopeOrPricingChanged,
        review,
      }
      : null,
  };
}

export type OfflinePublicDocPreviewEnvelope = {
  previewId: string;
  status: 'pending' | 'ready' | 'too-large';
  payload?: Record<string, any>;
};

export function createOfflinePublicDocPreviewId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function offlinePublicDocPreviewHref(previewId: string): string {
  const base = String(process.env.PUBLIC_URL || '').replace(/\/+$/, '');
  return `${base}${OFFLINE_PUBLIC_DOC_PREVIEW_PATH}?preview=${encodeURIComponent(previewId)}`;
}

export function readOfflinePublicDocPreviewEnvelope(): OfflinePublicDocPreviewEnvelope | null {
  try {
    const raw = localStorage.getItem(OFFLINE_PUBLIC_DOC_PREVIEW_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const previewId = String(parsed.previewId || '').trim();
    const status = parsed.status;
    if (!previewId || (status !== 'pending' && status !== 'ready' && status !== 'too-large')) return null;
    return {
      previewId,
      status,
      payload: parsed.payload && typeof parsed.payload === 'object' ? parsed.payload : undefined,
    };
  } catch {
    return null;
  }
}

export function markOfflinePublicDocPreviewPending(previewId: string): void {
  localStorage.setItem(
    OFFLINE_PUBLIC_DOC_PREVIEW_STORAGE_KEY,
    JSON.stringify({ previewId, status: 'pending' }),
  );
}

/** Save the preview for a tab that is already open on the preview route. */
export function publishOfflinePublicDocPreview(
  previewId: string,
  payload: unknown,
): 'stored' | 'too-large' {
  try {
    localStorage.setItem(
      OFFLINE_PUBLIC_DOC_PREVIEW_STORAGE_KEY,
      JSON.stringify({ previewId, status: 'ready', payload }),
    );
    return 'stored';
  } catch {
    try {
      localStorage.setItem(
        OFFLINE_PUBLIC_DOC_PREVIEW_STORAGE_KEY,
        JSON.stringify({ previewId, status: 'too-large' }),
      );
    } catch {
      // The workspace modal still has the preview.
    }
    return 'too-large';
  }
}
